'use client'

import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { requestFileHref, type FulfillmentRequestApi } from '@entities/fulfillment-request'
import { Button } from '@ui/base/button'
import { Field, FieldLabel, FieldTitle } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { Item, ItemActions, ItemButton, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@ui/base/item'
import { FileUpIcon, PaperclipIcon } from 'lucide-react'

/** Файлы меньше мегабайта показываем в килобайтах: «0 МБ» ни о чём не говорит. */
const sizeLabel = (size: number) =>
  size < 1024 * 1024
    ? `${Math.max(Math.round(size / 1024), 1)} КБ`
    : `${(size / (1024 * 1024)).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} МБ`

const inputId = 'request-files'
/** Те же типы принимает и сервер: остальное он отклонит, поэтому не даём выбрать их и здесь. */
const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']

/**
 * Фотография товара показывается сразу после выбора: продавец видит, что приложил именно тот снимок.
 * Адрес картинки живёт в памяти браузера, поэтому освобождаем его, когда файл убрали из списка.
 */
const PhotoPreview = ({ file }: { file: File }) => {
  const preview = useMemo(() => URL.createObjectURL(file), [file])

  useEffect(() => () => URL.revokeObjectURL(preview), [preview])

  // eslint-disable-next-line @next/next/no-img-element -- размеры снимка заранее неизвестны
  return <img src={preview} alt={file.name} />
}

type FilesFieldProps = {
  files: File[]
  maxCount: number
  onChange: (files: File[]) => void
  /** Заявка уже создана: список остаётся на виду, но менять его нельзя — грузятся именно эти файлы. */
  isLocked?: boolean
}

/** Файлы к заявке: перетаскивание, список выбранного и удаление по одному. */
export const FilesField = ({ files, maxCount, onChange, isLocked = false }: FilesFieldProps) => {
  const input = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  // Поле очищаем перед открытием: иначе повторный выбор того же файла не даёт события change.
  const openPicker = () => {
    if (input.current) {
      input.current.value = ''
      input.current.click()
    }
  }

  const add = (added: FileList | null) => onChange([...files, ...Array.from(added ?? [])].slice(0, maxCount))

  const dropFiles = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    add(event.dataTransfer.files)
  }

  return (
    <Field>
      <FieldLabel htmlFor={inputId}>Файлы: фото товара, спецификация, макет этикетки</FieldLabel>
      <Input
        ref={input}
        id={inputId}
        type="file"
        multiple
        accept={allowedTypes.join(',')}
        className="sr-only"
        onChange={(event) => add(event.target.files)}
      />
      {files.map((file, index) => (
        <Item key={`${file.name}-${index}`} variant="outline" size="sm">
          {file.type.startsWith('image/') ? (
            <ItemMedia variant="image">
              <PhotoPreview file={file} />
            </ItemMedia>
          ) : (
            <ItemMedia variant="icon">
              <PaperclipIcon />
            </ItemMedia>
          )}
          <ItemContent className="flex-none">
            <ItemTitle>{file.name}</ItemTitle>
            <ItemDescription>{sizeLabel(file.size)}</ItemDescription>
          </ItemContent>
          {!isLocked && (
            <ItemActions>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(files.filter((_, position) => position !== index))}
              >
                Убрать
              </Button>
            </ItemActions>
          )}
        </Item>
      ))}
      {!isLocked && files.length < maxCount && (
        // Зона выбора — настоящая кнопка: до неё нужно доходить с клавиатуры, а не только мышью.
        <Item
          render={<ItemButton />}
          variant="outline"
          size="sm"
          className={`w-full cursor-pointer border-dashed text-left ${dragging ? 'border-primary bg-accent/40' : ''}`}
          onClick={openPicker}
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={dropFiles}
        >
          <ItemMedia variant="icon">
            <FileUpIcon />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>Перетащите файлы сюда или выберите на диске</ItemTitle>
            <ItemDescription>
              JPEG, PNG, WebP или PDF, не больше 10 МБ каждый. Всего до {maxCount} файлов.
            </ItemDescription>
          </ItemContent>
        </Item>
      )}
    </Field>
  )
}

type SavedFilesFieldProps = {
  requestId: string
  files: FulfillmentRequestApi.File[]
}

/** Сохранённые файлы правки не меняются вместе с остальными полями: их можно только открыть. */
export const SavedFilesField = ({ requestId, files }: SavedFilesFieldProps) => (
  <Field>
    <FieldTitle>Уже приложенные файлы</FieldTitle>
    {files.length === 0 ? (
      <Item variant="muted" size="sm">
        <ItemContent>
          <ItemTitle>К заявке пока ничего не приложено</ItemTitle>
        </ItemContent>
      </Item>
    ) : (
      files.map((file) => (
        <Item key={file.id} variant="outline" size="sm">
          <ItemMedia variant="icon">
            <PaperclipIcon />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>
              <Button
                variant="link"
                size="sm"
                className="justify-start"
                render={<a href={requestFileHref(requestId, file.id)} target="_blank" rel="noopener noreferrer" />}
              >
                {file.name}
              </Button>
            </ItemTitle>
            <ItemDescription>{sizeLabel(file.size)}</ItemDescription>
          </ItemContent>
        </Item>
      ))
    )}
  </Field>
)
